import React, { useState } from 'react';
import { Globe, Plus, Trash2, Check, X, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';

const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' }
];

const LanguageManager = ({ site, onSiteUpdate, planInfo }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [loading, setLoading] = useState(false);

  const defaultLanguage = site?.defaultLanguage || 'en';
  const currentLanguages = site?.languages || [];
  const multilingualEnabled = site?.multilingual?.enabled || false;

  // Calculate current language count (default + active additional languages)
  const currentLanguageCount = 1 + currentLanguages.filter(lang => lang.isActive).length;
  const maxLanguages = planInfo?.limits?.languages?.max || 1;
  const canAddLanguages = maxLanguages === 'Unlimited' || currentLanguageCount < maxLanguages;

  // Get available languages that aren't already added
  const availableToAdd = AVAILABLE_LANGUAGES.filter(lang => 
    lang.code !== defaultLanguage && 
    !currentLanguages.find(cl => cl.code === lang.code)
  );

  const handleAddLanguage = async () => {
    if (!selectedLanguage) return;

    // Check plan limits before adding
    if (!canAddLanguages) {
      toast.error(`Your ${planInfo?.plan?.name || 'current'} plan allows maximum ${maxLanguages} language(s) per site`);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/sites/${site._id}/languages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          language: selectedLanguage
        })
      });

      if (response.ok) {
        const updatedSite = await response.json();
        onSiteUpdate(updatedSite);
        toast.success(`${AVAILABLE_LANGUAGES.find(l => l.code === selectedLanguage)?.name} added successfully`);
        setIsAdding(false);
        setSelectedLanguage('');
      } else {
        const error = await response.json();
        if (error.error === 'Language limit reached') {
          toast.error(error.message || 'Language limit reached');
        } else {
          toast.error(error.error || 'Failed to add language');
        }
      }
    } catch (error) {
      console.error('Error adding language:', error);
      toast.error('Failed to add language');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLanguage = async (langCode) => {
    if (langCode === defaultLanguage) {
      toast.error('Cannot remove default language');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/sites/${site._id}/languages/${langCode}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const updatedSite = await response.json();
        onSiteUpdate(updatedSite);
        const langName = AVAILABLE_LANGUAGES.find(l => l.code === langCode)?.name || langCode;
        toast.success(`${langName} removed successfully`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to remove language');
      }
    } catch (error) {
      console.error('Error removing language:', error);
      toast.error('Failed to remove language');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLanguage = async (langCode, isActive) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/sites/${site._id}/languages/${langCode}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ isActive: !isActive })
      });

      if (response.ok) {
        const updatedSite = await response.json();
        onSiteUpdate(updatedSite);
        const langName = AVAILABLE_LANGUAGES.find(l => l.code === langCode)?.name || langCode;
        toast.success(`${langName} ${!isActive ? 'enabled' : 'disabled'}`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update language');
      }
    } catch (error) {
      console.error('Error updating language:', error);
      toast.error('Failed to update language');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMultilingual = async (enabled) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/sites/${site._id}/multilingual`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ enabled })
      });

      if (response.ok) {
        const updatedSite = await response.json();
        onSiteUpdate(updatedSite);
        toast.success(`Multilingual ${enabled ? 'enabled' : 'disabled'}`);
      } else {
        const error = await response.json();
        if (error.error === 'Language limit exceeded') {
          toast.error(error.message || 'Language limit exceeded');
        } else {
          toast.error(error.error || 'Failed to update multilingual settings');
        }
      }
    } catch (error) {
      console.error('Error updating multilingual settings:', error);
      toast.error('Failed to update multilingual settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMultilingualSetting = async (setting, value) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/sites/${site._id}/multilingual`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ [setting]: value })
      });

      if (response.ok) {
        const updatedSite = await response.json();
        onSiteUpdate(updatedSite);
        
        const settingNames = {
          autoTranslate: 'Auto-translate on publish',
          showLanguageSwitcher: 'Language switcher'
        };
        
        toast.success(`${settingNames[setting]} ${value ? 'enabled' : 'disabled'}`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update setting');
      }
    } catch (error) {
      console.error('Error updating multilingual setting:', error);
      toast.error('Failed to update setting');
    } finally {
      setLoading(false);
    }
  };


  const getLanguageInfo = (langCode) => {
    return AVAILABLE_LANGUAGES.find(l => l.code === langCode) || 
           { code: langCode, name: langCode.toUpperCase(), flag: '🌐' };
  };

  const defaultLangInfo = getLanguageInfo(defaultLanguage);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Globe className="w-4 h-4 mr-2 text-gray-500" />
          <h4 className="text-xs font-medium text-gray-900">Languages</h4>
        </div>
        {currentLanguages.length > 0 && (
          <label className="relative inline-block w-9 h-5 cursor-pointer">
            <input 
              type="checkbox" 
              checked={multilingualEnabled}
              onChange={(e) => handleToggleMultilingual(e.target.checked)}
              disabled={loading}
              className="sr-only"
            />
            <span className={`absolute inset-0 rounded-full transition-colors ${
              multilingualEnabled ? 'bg-purple-600' : 'bg-gray-300'
            }`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                multilingualEnabled ? 'translate-x-4' : 'translate-x-0'
              }`} />
            </span>
          </label>
        )}
      </div>

      {/* Default Language */}
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center">
          <span className="text-sm mr-2">{defaultLangInfo.flag}</span>
          <div>
            <div className="text-xs font-medium text-gray-900">{defaultLangInfo.name}</div>
            <div className="text-xs text-gray-500">Default language</div>
          </div>
        </div>
        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">Primary</span>
      </div>

      {/* Additional Languages */}
      {currentLanguages.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-gray-500">Additional languages</div>
          {currentLanguages.map((lang) => {
            const langInfo = getLanguageInfo(lang.code);
            return (
              <div key={lang.code} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center">
                  <span className="text-sm mr-2">{langInfo.flag}</span>
                  <div>
                    <div className="text-xs font-medium text-gray-900">{langInfo.name}</div>
                    <div className="text-xs text-gray-500">
                      {lang.isActive ? 'Active' : 'Inactive'} • Added {new Date(lang.addedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleToggleLanguage(lang.code, lang.isActive)}
                    disabled={loading}
                    className="p-1 text-gray-400 hover:text-gray-600"
                    title={lang.isActive ? 'Disable language' : 'Enable language'}
                  >
                    {lang.isActive ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  </button>
                  <button
                    onClick={() => handleRemoveLanguage(lang.code)}
                    disabled={loading}
                    className="p-1 text-red-400 hover:text-red-600"
                    title="Remove language"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Multilingual Settings */}
      {currentLanguages.length > 0 && (
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-xs font-medium text-gray-900 mb-2">Settings</div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label htmlFor="showLanguageSwitcher" className="text-xs text-gray-700">
                Show language switcher on website
              </label>
              <input
                type="checkbox"
                id="showLanguageSwitcher"
                checked={site.multilingual?.showLanguageSwitcher !== false}
                onChange={(e) => handleToggleMultilingualSetting('showLanguageSwitcher', e.target.checked)}
                className="h-3 w-3 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
            </div>
            <div className="pt-2 border-t border-gray-200">
              <div className="text-xs text-gray-600 bg-gray-100 p-2 rounded">
                <div className="font-medium text-gray-800 mb-1">Translation Management</div>
                <div className="text-gray-700">
                  Use the "Manage Translations" button below to translate content to your languages.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Language */}
      {!isAdding && availableToAdd.length > 0 && (
        <div className="space-y-2">
          <button
            onClick={() => setIsAdding(true)}
            disabled={loading || !canAddLanguages}
            className={`w-full flex items-center justify-center p-3 border-2 border-dashed rounded-lg transition-colors ${
              canAddLanguages
                ? 'border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600'
                : 'border-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            title={!canAddLanguages ? `Language limit reached (${currentLanguageCount}/${maxLanguages})` : 'Add a new language'}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add language
          </button>
          {planInfo && (
            <div className="text-xs text-gray-500 text-center">
              {currentLanguageCount}/{maxLanguages === 'Unlimited' ? '∞' : maxLanguages} languages used
              {planInfo.plan && (
                <span className="text-purple-600 font-medium"> • {planInfo.plan.name} plan</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add Language Form */}
      {isAdding && (
        <div className="p-3 border border-gray-200 rounded-lg space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Select Language
            </label>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="w-full text-xs border border-gray-300 rounded px-2 py-1"
            >
              <option value="">Choose a language...</option>
              {availableToAdd.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>

          <div className="text-xs text-gray-600 mb-3 p-2 bg-blue-50 border border-blue-200 rounded">
            <div className="font-medium text-blue-800 mb-1">Translation Setup</div>
            <div className="text-blue-700">
              Language will be added with empty translations. Use the Translation Manager to add content after adding the language.
            </div>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={handleAddLanguage}
              disabled={!selectedLanguage || loading}
              className="flex-1 flex items-center justify-center px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 disabled:opacity-50"
            >
              <Check className="w-3 h-3 mr-1" />
              Add
            </button>
            <button
              onClick={() => {
                setIsAdding(false);
                setSelectedLanguage('');
              }}
              disabled={loading}
              className="flex-1 flex items-center justify-center px-3 py-1 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50"
            >
              <X className="w-3 h-3 mr-1" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {availableToAdd.length === 0 && !isAdding && currentLanguages.length > 0 && (
        <div className="text-xs text-gray-500 text-center py-2">
          All supported languages have been added
        </div>
      )}
    </div>
  );
};

export default LanguageManager;